<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { companySettingsApi, type BillingData } from '@/features/company-settings/api/companySettingsApi';
import { usePlanDialogStore } from '@/shared/stores/planDialog';

const router = useRouter();

const planDialogStore = usePlanDialogStore();
const billingData = ref<BillingData | null>(null);
const loading = ref(false);

const isTrial = computed(() => billingData.value?.subscription?.isTrial ?? true);

const planLabel = computed(() => {
  if (!billingData.value?.subscription) return 'Trial';
  if (isTrial.value) return 'Trial';
  const planName = billingData.value.subscription.planName || '';
  // Capitalize first letter
  return planName.charAt(0).toUpperCase() + planName.slice(1);
});

const activeCount = computed(() => billingData.value?.entityUsage?.activeCount ?? 0);
const entityLimit = computed(() => billingData.value?.entityUsage?.limit ?? 0);

// 구독 상태(EXPIRED/SUSPENDED/CANCELLED) — BillingTab의 상태 배지와 동일 기준(#status-consistency).
// 항상 눈에 보이는 헤더 배지가 상태와 무관하게 계속 초록이면 만료/정지를 놓치기 쉬워 색을 상태에 연동.
const subStatus = computed(() => (billingData.value?.subscription?.status || '').toUpperCase());
const isExpired = computed(() => subStatus.value === 'EXPIRED');
const isSuspended = computed(() => subStatus.value === 'SUSPENDED');
const isCancelled = computed(() => subStatus.value === 'CANCELLED');
const isInactive = computed(() => isExpired.value || isSuspended.value || isCancelled.value);

// 갱신 임박(60일 이내, 아직 활성) — 갱신 허용창(B안, RENEWAL_WINDOW_DAYS)과 동일 기준.
const RENEWAL_WINDOW_DAYS = 60;
const daysUntilExpiry = computed<number | null>(() => {
  const raw = billingData.value?.subscription?.endDate;
  if (!raw) return null;
  let s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = s.replace(' ', 'T');
  if (s.includes('T') && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const end = new Date(s);
  if (isNaN(end.getTime())) return null;
  return Math.ceil((end.getTime() - Date.now()) / 86_400_000);
});
const isRenewalDue = computed(() => {
  if (isInactive.value || isTrial.value) return false;
  const d = daysUntilExpiry.value;
  return d != null && d >= 0 && d <= RENEWAL_WINDOW_DAYS;
});

const badgeColor = computed(() => {
  if (isTrial.value) return 'warning';
  if (isExpired.value || isCancelled.value) return 'error';
  if (isSuspended.value) return 'warning';
  if (isRenewalDue.value) return 'warning';
  return 'success';
});
const statusSuffix = computed(() => {
  if (isExpired.value) return 'Expired';
  if (isSuspended.value) return 'Suspended';
  if (isCancelled.value) return 'Cancelled';
  if (isRenewalDue.value) return `Renews in ${daysUntilExpiry.value}d`;
  return '';
});

const fetchBilling = async () => {
  loading.value = true;
  try {
    const res = await companySettingsApi.getBilling() as any;
    billingData.value = res.data || res;
  } catch (error) {
    console.error('[HeaderPlanBadge] Failed to fetch billing info:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchBilling);

// 결제 완료 시 상단 배지 자동 갱신
watch(() => planDialogStore.refreshKey, () => {
  fetchBilling();
});
</script>

<template>
  <div class="header-plan-badge d-flex align-center" v-if="!loading">
    <v-chip
      :color="badgeColor"
      size="small"
      variant="flat"
      class="plan-chip"
      label
      style="cursor: pointer"
      @click="router.push('/settings/billing')"
    >
      <v-icon size="14" start>mdi-shield-check-outline</v-icon>
      <template v-if="isTrial">
        Trial
      </template>
      <template v-else>
        {{ planLabel }}
        <span class="entity-count ms-1">{{ activeCount }}/{{ entityLimit }}</span>
        <span v-if="isInactive || isRenewalDue" class="ms-1">· {{ statusSuffix }}</span>
      </template>
    </v-chip>
  </div>
  <div v-else class="header-plan-badge d-flex align-center">
    <v-progress-circular size="16" width="2" indeterminate color="white" />
  </div>
</template>

<style scoped>
.header-plan-badge {
  margin-left: 8px;
}

.plan-chip {
  font-size: 11px !important;
  font-weight: 600;
  height: 24px !important;
  letter-spacing: 0.3px;
}

.entity-count {
  font-weight: 700;
  opacity: 0.9;
}
</style>
